import useMileage from '@di-shared/features/mileage/useMileage';
import {useSuspenseQuery} from '@tanstack/react-query';
import {getQueryKey} from '@shared/queryKeyMap';
import {diApi} from '@di-shared/api/di';
import useOdometer from '@di-shared/features/mileage/useOdometer';

export enum SafeDrivingType {
  GOOD,
  WARNING,
  BAD,
  IMPOSSIBLE_2024,
  NO_DRIVING,
  ETC,
}

export enum SafeDrivingChallengeType {
  FIVE_THOUSAND,
  THREE_THOUSAND,
  INSUFFICIENT_SCORE,
  INSUFFICIENT_ODOMETER,
}

export enum SafeDrivingChallengeCondition {
  MET,
  NOT_MET,
  NONE,
}

const SAFE_DRIVING_SCORE_BEST_THRESHOLD = 95;
const SAFE_DRIVING_SCORE_GOOD_THRESHOLD = 85;
const SAFE_DRIVING_SCORE_WARNING_THRESHOLD = 40;
const SAFE_DRIVING_BURST_COUNT_THRESHOLD = 200;
const ODOMETER_THRESHOLD_90_DAYS = 500;

function getScore95ConditionStatus(safetyDrvScore: number) {
  if (safetyDrvScore >= SAFE_DRIVING_SCORE_BEST_THRESHOLD) {
    return SafeDrivingChallengeCondition.MET;
  }
  return SafeDrivingChallengeCondition.NONE;
}

function getScore85ConditionStatus(safetyDrvScore: number) {
  if (safetyDrvScore >= SAFE_DRIVING_SCORE_BEST_THRESHOLD) {
    return SafeDrivingChallengeCondition.NONE;
  }
  if (safetyDrvScore >= SAFE_DRIVING_SCORE_GOOD_THRESHOLD) {
    return SafeDrivingChallengeCondition.MET;
  }
  return SafeDrivingChallengeCondition.NOT_MET;
}

function getDrivingDistanceConditionStatus(rangeDrvDist: number) {
  if (rangeDrvDist >= ODOMETER_THRESHOLD_90_DAYS) {
    return SafeDrivingChallengeCondition.MET;
  }
  return SafeDrivingChallengeCondition.NOT_MET;
}

function getSafeDrivingChallengeType(safetyDrvScore: number, rangeDrvDist: number) {
  if (safetyDrvScore >= SAFE_DRIVING_SCORE_GOOD_THRESHOLD) {
    if (rangeDrvDist < ODOMETER_THRESHOLD_90_DAYS) {
      return SafeDrivingChallengeType.INSUFFICIENT_ODOMETER;
    }
    if (safetyDrvScore >= SAFE_DRIVING_SCORE_BEST_THRESHOLD) {
      return SafeDrivingChallengeType.FIVE_THOUSAND;
    }
    return SafeDrivingChallengeType.THREE_THOUSAND;
  }
  return SafeDrivingChallengeType.INSUFFICIENT_SCORE;
}

function getIsDeadScore(safetyDrvScore: number, brstAccCount: number, brstDecCount: number) {
  return safetyDrvScore <= SAFE_DRIVING_SCORE_WARNING_THRESHOLD && brstAccCount + brstDecCount >= SAFE_DRIVING_BURST_COUNT_THRESHOLD;
}

function getSafeDrivingType({
  isChallenge24Impossible,
  safetyDrvScore,
  isDeadScore,
  rangeDrvDist,
}: {
  isChallenge24Impossible: boolean;
  safetyDrvScore: number;
  isDeadScore: boolean;
  rangeDrvDist: number;
}) {
  if (isChallenge24Impossible) {
    return SafeDrivingType.IMPOSSIBLE_2024;
  }
  if (!rangeDrvDist && !safetyDrvScore) {
    return SafeDrivingType.NO_DRIVING;
  }
  if (safetyDrvScore >= SAFE_DRIVING_SCORE_GOOD_THRESHOLD) {
    return SafeDrivingType.GOOD;
  }
  if (isDeadScore) {
    return SafeDrivingType.BAD;
  }
  return SafeDrivingType.WARNING;
}

function useMileageSafeDriving(vin: string) {
  const {isChallenge24Impossible, challengeDDay} = useMileage(vin);
  const {updatedDate} = useOdometer(vin);

  const {
    data: {safetyDrvScore, brstAccCount, brstDecCount, rangeDrvDist, safetyDrvScoreDiff},
  } = useSuspenseQuery({
    queryKey: [getQueryKey(diApi.ubiController.getUbi4), vin],
    queryFn: () => diApi.ubiController.getUbi4({vin}),
    select: (data) => data.data,
  });

  const isDeadScore = getIsDeadScore(safetyDrvScore, brstAccCount, brstDecCount);
  const safeDrivingType = getSafeDrivingType({isChallenge24Impossible, safetyDrvScore, isDeadScore, rangeDrvDist});
  const safeDrivingChallengeType = getSafeDrivingChallengeType(safetyDrvScore, rangeDrvDist);
  const score95ConditionStatus = getScore95ConditionStatus(safetyDrvScore);
  const score85ConditionStatus = getScore85ConditionStatus(safetyDrvScore);
  const drivingDistanceConditionStatus = getDrivingDistanceConditionStatus(rangeDrvDist);

  return {
    safeDrivingType,
    safetyDrvScore,
    safetyDrvScoreDiff,
    challengeDDay,
    rangeDrvDist,
    safeDrivingChallengeType,
    score95ConditionStatus,
    score85ConditionStatus,
    drivingDistanceConditionStatus,
    updatedDate,
  };
}

export default useMileageSafeDriving;
